import { getMatomoConfig } from "@/lib/matomo/config";
import { readMatomoConsent, writeMatomoConsent } from "@/lib/matomo/consent";

type PaqCommand = unknown[];

declare global {
  interface Window {
    // Matomo official queue name
    _paq?: PaqCommand[];
  }
}

let scriptInjected = false;
let routerBound = false;

function getPaq(): PaqCommand[] {
  // Matomo's global queue is named `_paq` by design (official tracker API).
  // eslint-disable-next-line no-underscore-dangle -- Matomo _paq
  const w = window as Window & { _paq?: PaqCommand[] };
  // eslint-disable-next-line no-underscore-dangle -- Matomo _paq
  if (!w._paq) {
    // eslint-disable-next-line no-underscore-dangle -- Matomo _paq
    w._paq = [];
  }
  // eslint-disable-next-line no-underscore-dangle -- Matomo _paq
  return w._paq;
}

function push(...args: unknown[]): void {
  if (typeof window === "undefined") {
    return;
  }
  getPaq().push(args);
}

/** Load Matomo JS once with requireConsent. No-op when disabled. */
export function initMatomoTracker(): void {
  if (typeof window === "undefined") {
    return;
  }
  const config = getMatomoConfig();
  if (!config.enabled || scriptInjected) {
    return;
  }

  getPaq();
  push("requireConsent");
  push("setTrackerUrl", config.trackerUrl);
  push("setSiteId", config.siteId);
  push("enableLinkTracking");

  const script = document.createElement("script");
  script.async = true;
  script.src = config.scriptUrl;
  const first = document.getElementsByTagName("script")[0];
  first?.parentNode?.insertBefore(script, first);
  scriptInjected = true;

  if (readMatomoConsent() === "granted") {
    grantMatomoConsent({ trackCurrentPage: true });
  }
}

export function grantMatomoConsent(options?: {
  trackCurrentPage?: boolean;
}): void {
  writeMatomoConsent("granted");
  push("setConsentGiven");
  if (options?.trackCurrentPage !== false) {
    trackMatomoPageView();
  }
}

export function revokeMatomoConsent(): void {
  writeMatomoConsent("denied");
  push("forgetConsentGiven");
}

export function trackMatomoPageView(customUrl?: string, title?: string): void {
  const config = getMatomoConfig();
  if (!config.enabled) {
    return;
  }
  const url =
    customUrl ??
    `${window.location.pathname}${window.location.search}${window.location.hash}`;
  push("setCustomUrl", url);
  push("setDocumentTitle", title ?? document.title);
  push("trackPageView");
}

/** Same semantic as reader `usr_email`: account email plaintext. */
export function setMatomoUserId(email: string | null | undefined): void {
  const config = getMatomoConfig();
  if (!config.enabled) {
    return;
  }
  const trimmed = email?.trim();
  if (trimmed) {
    push("setUserId", trimmed);
  } else {
    push("resetUserId");
  }
}

/** Bind SPA navigations (TanStack Router). Call once with the app router. */
export function bindMatomoToRouter(router: {
  subscribe: (
    eventType: "onResolved",
    fn: (event: {
      toLocation: { pathname: string; searchStr: string; hash: string };
      hrefChanged: boolean;
      pathChanged: boolean;
    }) => void
  ) => () => void;
}): () => void {
  if (routerBound || !getMatomoConfig().enabled) {
    return () => undefined;
  }
  routerBound = true;

  // First paint is tracked on consent grant; here only real navigations.
  return router.subscribe("onResolved", (event) => {
    if (readMatomoConsent() !== "granted") {
      return;
    }
    if (!event.pathChanged && !event.hrefChanged) {
      return;
    }
    const { pathname, searchStr, hash } = event.toLocation;
    trackMatomoPageView(`${pathname}${searchStr}${hash}`);
  });
}
